import React, { useCallback, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useSession, useSupabaseClient } from '@supabase/auth-helpers-react';
import Modal from '../../components/Modal';
import Field from '../../components/Field';
import TextInput from '../../components/TextInput';
import { ProjectDetailReopen } from '../../components/ProjectDetailModal/config';
import { notifyReadOnly } from '../../utils/supabaseErrors';
import { createDocument } from './createDocument';
import { AvailableTemplate, DEFAULT_DOCUMENT_TITLES } from './config';

interface UseCreateDocumentOptions {
  projectId: number;
  stageId: number | null;
  isReadOnly: boolean;
  /** Close the project modal before the full-screen editor takes over. */
  onClose: () => void;
  /** What the editor needs to put this modal back where it was. */
  reopenIdentity: Omit<ProjectDetailReopen, 'tab'>;
  /** Which tab to reopen on. */
  returnTab: ProjectDetailReopen['tab'];
}

/**
 * Raise a document from a template, name it (unless its title is
 * standardised), and open it in the builder.
 *
 * Shared because two surfaces of the project modal do this: the Documents tab
 * raises any template, and Construction Admin raises the one that records an
 * inspection. They were the same ninety lines twice, and the second copy would
 * have drifted the first time the title prompt changed.
 *
 * Returns the dialog as a node -- the caller decides where it sits in its own
 * tree, and gets `startCreate` to open it.
 */
export function useCreateDocument({
  projectId,
  stageId,
  isReadOnly,
  onClose,
  reopenIdentity,
  returnTab,
}: UseCreateDocumentOptions) {
  const supabase = useSupabaseClient();
  const session = useSession();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();

  const [pendingTemplate, setPendingTemplate] = useState<AvailableTemplate | null>(null);
  const [newTitle, setNewTitle] = useState('');
  const [creatingTemplateId, setCreatingTemplateId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const openDocument = useCallback((id: string) => {
    // The editor is a full-screen route; close the modal first so the user does
    // not return to a stale modal behind it.
    //
    // Where to come back to travels with the navigation: the page the user was
    // on, plus what it needs to put the modal back on this tab. Without it the
    // editor's close button drops them on /documents, which is not where they
    // were and not what they were doing.
    onClose();
    navigate(`/documents/${id}`, {
      state: {
        returnTo: {
          pathname: location.pathname,
          search: location.search,
          state: { reopen: { ...reopenIdentity, tab: returnTab } as ProjectDetailReopen },
        },
      },
    });
  }, [onClose, navigate, location.pathname, location.search, reopenIdentity, returnTab]);

  // The title is asked for here rather than defaulted. It is part of the
  // document number ("SI2 - Footing Inspection"), and for a template that
  // records an inspection it also becomes that inspection's description -- so
  // "Untitled", which this used to create, is not good enough. The exception is
  // a template whose title is standardised (DEFAULT_DOCUMENT_TITLES): see
  // startCreate.
  const create = useCallback(async (template: AvailableTemplate, title: string): Promise<boolean> => {
    if (!session?.user || isReadOnly) return false;

    setCreatingTemplateId(template.id);
    setError(null);
    try {
      const newId = await createDocument(supabase, session.user.id, {
        templateId: template.id,
        projectId,
        stageId,
        title,
        chrome: template.chrome,
        templateCode: template.code,
        requiresCertifiedSignatory: (template.signature_slots || []).some(sl => sl.signer === 'certified'),
      });
      // The builder's left panel lists this job's documents from a cache that
      // outlives the editor's remount. Without this the document just created
      // is missing from the panel of the editor it just opened. The register
      // is the same kind of cache, fresh for two minutes, so the document was
      // also missing from /documents until a reload.
      void queryClient.invalidateQueries({ queryKey: ['document_siblings', projectId, stageId] });
      void queryClient.invalidateQueries({ queryKey: ['get_filtered_documents'] });
      openDocument(newId);
      return true;
    } catch (err) {
      console.error('Error creating document:', err);
      notifyReadOnly(err);
      setError((err as Error)?.message || 'Could not create the document.');
      setCreatingTemplateId(null);
      return false;
    }
  }, [session, isReadOnly, supabase, projectId, stageId, openDocument, queryClient]);

  const confirmCreate = useCallback(async () => {
    if (!pendingTemplate || !newTitle.trim()) return;
    // The prompt stays up until the document actually exists. Tearing it down
    // first threw away what the user typed whenever creation failed.
    const created = await create(pendingTemplate, newTitle.trim());
    if (created) {
      setPendingTemplate(null);
      setNewTitle('');
    }
  }, [pendingTemplate, newTitle, create]);

  const cancelCreate = useCallback(() => {
    setPendingTemplate(null);
    setNewTitle('');
    setError(null);
  }, []);

  /**
   * Raise a document of this template: straight into the builder when its
   * title is standardised, otherwise through the title prompt.
   *
   * The certificates are always titled the same, so asking only made the user
   * confirm a field nobody changes. The rare one-off is renamed in the
   * builder's Fields tab (Report title), which carries the title too.
   */
  const startCreate = useCallback((template: AvailableTemplate) => {
    setError(null);
    const standard = DEFAULT_DOCUMENT_TITLES[template.code ?? ''];
    if (standard) {
      void create(template, standard);
      return;
    }
    setPendingTemplate(template);
    setNewTitle('');
  }, [create]);

  const dialog = pendingTemplate ? (
    <Modal
      isOpen
      onClose={cancelCreate}
      title={`New ${pendingTemplate.name}`}
      size="sm"
      hasFooter
      className="documents-tab__new-modal"
      footerContent={
        <div className="pill-row">
          <button type="button" className="pill-btn pill-btn--ghost" onClick={cancelCreate}>
            Cancel
          </button>
          <button
            type="button"
            className="pill-btn pill-btn--primary"
            disabled={!newTitle.trim() || creatingTemplateId !== null}
            onClick={() => void confirmCreate()}
          >
            {creatingTemplateId ? 'Creating…' : 'Create'}
          </button>
        </div>
      }
    >
      <div className="form-ui">
        <Field
          label="Title"
          required
          htmlFor="documents-tab-new-title"
          hint={pendingTemplate.creates_inspection
            ? 'Used in the document title and as the description of the inspection this records.'
            : 'Used in the document title.'}
          error={error}
        >
          <TextInput
            id="documents-tab-new-title"
            autoFocus
            value={newTitle}
            placeholder={pendingTemplate.creates_inspection ? 'e.g. Footing Inspection' : 'Document title'}
            onChange={e => setNewTitle(e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Enter' && newTitle.trim()) {
                e.preventDefault();
                void confirmCreate();
              }
            }}
          />
        </Field>
      </div>
    </Modal>
  ) : null;

  return { startCreate, openDocument, creatingTemplateId, dialog, error };
}
